import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Pencil, Archive, X, Save, User, 
  Search, Upload, Star, Instagram, Facebook, Phone, Youtube
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArtistsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    image: '',
    category: 'Artista',
    description: '',
    featured: false,
    social_links: {
      instagram: '',
      facebook: '',
      youtube: '',
      phone: ''
    }
  });

  const categories = ['Artista', 'Arrocha / Brega', 'Arrocha / Romântico', 'Arrocha / Sofrência', 'Pagode', 'Forró', 'Sertanejo', 'Axé', 'MPB', 'Rock', 'Pop'];

  const fetchArtists = async () => {
    try {
      const response = await axios.get(`${API}/artists`, {
        headers: getAuthHeaders()
      });
      setArtists(response.data);
    } catch (error) {
      console.error('Erro ao buscar artistas:', error);
    }
    setLoading(false);
  };

  const openNewModal = () => {
    setEditingArtist(null);
    setFormData({
      name: '',
      image: '',
      category: 'Artista',
      description: '',
      featured: false,
      social_links: {
        instagram: '',
        facebook: '',
        youtube: '',
        phone: ''
      }
    });
    setShowModal(true);
  };

  useEffect(() => {
    fetchArtists();
    if (searchParams.get('action') === 'new') {
      openNewModal();
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEditModal = (artist) => {
    setEditingArtist(artist);
    setFormData({
      name: artist.name || '',
      image: artist.image || '',
      category: artist.category || 'Artista',
      description: artist.description || '',
      featured: artist.featured || false,
      social_links: {
        instagram: artist.social_links?.instagram || '',
        facebook: artist.social_links?.facebook || '',
        youtube: artist.social_links?.youtube || '',
        phone: artist.social_links?.phone || ''
      }
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        social_links: formData.social_links.instagram || formData.social_links.facebook || formData.social_links.youtube || formData.social_links.phone 
          ? formData.social_links 
          : null
      };

      if (editingArtist) {
        await axios.put(`${API}/artists/${editingArtist.id}`, dataToSend, {
          headers: getAuthHeaders()
        });
      } else {
        await axios.post(`${API}/artists`, dataToSend, {
          headers: getAuthHeaders()
        });
      }
      setShowModal(false);
      fetchArtists();
    } catch (error) {
      console.error('Erro ao salvar artista:', error);
      alert('Erro ao salvar artista');
    }
    setLoading(false);
  };

  const handleDelete = async (artist) => {
    const confirmed = window.confirm(
      `Arquivar "${artist.name}"?\n\nA/o artista será removida/o da seção "Nossos Artistas" no site público. Esta ação pode ser desfeita cadastrando o artista novamente pelo botão "Novo Artista".`
    );
    if (!confirmed) return;

    try {
      await axios.delete(`${API}/artists/${artist.id}`, {
        headers: getAuthHeaders()
      });
      fetchArtists();
    } catch (error) {
      console.error('Erro ao arquivar artista:', error);
      alert('Erro ao arquivar artista');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione uma imagem válida (JPG, PNG, GIF, etc)');
      return;
    }

    // Validar tamanho (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('A imagem é muito grande. O tamanho máximo é 10MB.');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({ ...formData, image: `${BACKEND_URL}${response.data.url}` });
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      if (error.response?.status === 401) {
        alert('Sessão expirada. Por favor, faça login novamente.');
      } else {
        alert('Erro ao fazer upload da imagem. Tente novamente.');
      }
    }
    setUploading(false);
    // Limpar o input para permitir upload do mesmo arquivo
    e.target.value = '';
  };

  const filteredArtists = artists.filter(artist => 
    artist.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    artist.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Artistas</h1>
          <p className="text-gray-400">Gerencie o elenco de artistas</p>
        </div>
        <button
          onClick={openNewModal}
          className="bg-gradient-to-r from-[#00F0FF] to-[#FF0099] text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 hover:opacity-90 transition-opacity"
        >
          <Plus size={20} />
          Novo Artista
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
        <input
          type="text"
          placeholder="Buscar artistas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg py-3 pl-12 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-[#00F0FF]"
        />
      </div>

      {/* Artists Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredArtists.length === 0 ? (
        <div className="text-center py-16">
          <User className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-400">Nenhum artista encontrado</p>
          <button
            onClick={openNewModal}
            className="mt-4 text-[#00F0FF] hover:underline"
          >
            Adicionar primeiro artista
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredArtists.map((artist) => (
            <div
              key={artist.id}
              className="bg-[#0a0a0a] border border-white/5 rounded-xl overflow-hidden group hover:border-white/10 transition-all"
            >
              <div className="relative h-64">
                <img
                  src={artist.image}
                  alt={artist.name}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                {artist.featured && (
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 bg-yellow-500 text-black text-xs font-bold rounded flex items-center gap-1">
                      <Star size={12} fill="currentColor" />
                      Destaque
                    </span>
                  </div>
                )}
                <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => openEditModal(artist)}
                    className="p-2 bg-white/20 backdrop-blur rounded-lg hover:bg-white/30 transition-colors"
                    title="Editar artista"
                    data-testid={`admin-edit-artist-${artist.id}`}
                  >
                    <Pencil size={16} className="text-white" />
                  </button>
                  <button
                    onClick={() => handleDelete(artist)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-amber-600/80 backdrop-blur rounded-lg hover:bg-amber-600 transition-colors"
                    title="Arquivar artista (remove do site público)"
                    data-testid={`admin-archive-artist-${artist.id}`}
                  >
                    <Archive size={16} className="text-white" />
                    <span className="text-white text-xs font-semibold">Arquivar</span>
                  </button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="inline-block px-2 py-1 bg-[#FF0099] text-white text-xs font-bold uppercase rounded mb-2">
                    {artist.category}
                  </span>
                  <h3 className="text-white font-bold text-lg">{artist.name}</h3>
                </div>
              </div>
              {artist.description && (
                <div className="p-4 border-t border-white/5">
                  <p className="text-gray-400 text-sm line-clamp-2">{artist.description}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-8">
          <div className="w-full max-w-2xl mx-4 bg-[#0a0a0a] rounded-2xl border border-white/10">
            {/* Modal Header */}
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingArtist ? 'Editar Artista' : 'Novo Artista'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                  placeholder="Nome do artista/banda"
                />
              </div>

              {/* Image */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Foto *</label>
                <div className="flex gap-4">
                  <input
                    type="text"
                    required
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    className="flex-1 bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                    placeholder="URL da foto ou faça upload"
                  />
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className={`px-4 py-3 bg-[#111] border rounded-lg flex items-center gap-2 transition-colors ${
                      uploading 
                        ? 'border-[#00F0FF] text-[#00F0FF] cursor-wait' 
                        : 'border-white/10 text-white hover:border-[#00F0FF]'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm">Enviando...</span>
                      </>
                    ) : (
                      <Upload size={20} />
                    )}
                  </button>
                </div>
                {formData.image && (
                  <div className="mt-4">
                    <p className="text-gray-500 text-xs mb-2">Preview da imagem:</p>
                    <img 
                      src={formData.image} 
                      alt="Preview" 
                      className="h-40 w-auto rounded-lg object-cover border border-white/10 bg-[#111]"
                      onError={(e) => {
                        e.target.style.opacity = '0.5';
                        e.target.alt = 'Erro ao carregar imagem';
                      }}
                      onLoad={(e) => {
                        e.target.style.opacity = '1';
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Categoria/Estilo *</label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Biografia</label>
                <textarea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-[#111] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#00F0FF] resize-none"
                  placeholder="Conte um pouco sobre o artista..."
                />
              </div>

              {/* Social Links */}
              <div>
                <label className="block text-gray-400 text-sm mb-3">Redes Sociais</label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Instagram size={20} className="text-pink-500" />
                    <input
                      type="text"
                      value={formData.social_links.instagram}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social_links: { ...formData.social_links, instagram: e.target.value }
                      })}
                      className="flex-1 bg-[#111] border border-white/10 rounded-lg py-2 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                      placeholder="@usuario"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Facebook size={20} className="text-blue-500" />
                    <input
                      type="text"
                      value={formData.social_links.facebook}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social_links: { ...formData.social_links, facebook: e.target.value }
                      })}
                      className="flex-1 bg-[#111] border border-white/10 rounded-lg py-2 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                      placeholder="facebook.com/..."
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Youtube size={20} className="text-red-500" />
                    <input
                      type="text"
                      value={formData.social_links.youtube}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social_links: { ...formData.social_links, youtube: e.target.value }
                      })}
                      className="flex-1 bg-[#111] border border-white/10 rounded-lg py-2 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                      placeholder="youtube.com/@canal ou link do canal"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={20} className="text-green-500" />
                    <input
                      type="text"
                      value={formData.social_links.phone}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social_links: { ...formData.social_links, phone: e.target.value }
                      })}
                      className="flex-1 bg-[#111] border border-white/10 rounded-lg py-2 px-4 text-white focus:outline-none focus:border-[#00F0FF]"
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                </div>
              </div>

              {/* Featured */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, featured: !formData.featured })}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    formData.featured ? 'bg-yellow-500' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    formData.featured ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <span className="text-gray-400 flex items-center gap-2">
                  <Star size={16} className={formData.featured ? 'text-yellow-500' : ''} />
                  {formData.featured ? 'Artista em Destaque' : 'Normal'}
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 border border-white/10 rounded-lg text-gray-400 hover:text-white hover:border-white/30 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 bg-gradient-to-r from-[#00F0FF] to-[#FF0099] rounded-lg text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save size={18} />
                      {editingArtist ? 'Salvar' : 'Adicionar'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistsPage;
